import type { LocalizedDict } from '../index'

// Shipping providers hub (ShippingHub, ProviderCard, CarriersSettings, AddressCheckCard).

export type ShippingProvidersStrings = {
  hub: {
    title: string
    description: string
    cheapestRule: string
    offRule: string
    internalRule: string
    channelsTitle: string
    channelsHint: string
    loading: string
    loadError: string
    carriersTitle: string
    carriersHint: string
    addressTitle: string
    addressHint: string
  }
  roles: {
    avocore: string
    byelabel: string
    shipdad: string
    uniuni: string
    stamps: string
    shipstation: string
    usps: string
  }
  uspsDirectLabel: string
  card: {
    requestFailed: (status: number) => string
    notConnected: string
    off: string
    active: string
    keys: string
    saved: string
    missingSr: string
    paidFrom: (account: string) => string
    balance: string
    keysSaved: (label: string) => string
    saveFailed: (label: string) => string
    isOn: (label: string) => string
    isOff: (label: string) => string
    switchFailed: (label: string) => string
    activeSwitch: (label: string) => string
    accountLabel: string
    savedPlaceholder: string
    pastePlaceholder: string
    cancel: string
    saving: string
    saveKeys: string
    connectionWorks: string
    connectionFailed: string
    cheapest: (amount: string) => string
    checked: (when: string) => string
    never: string
    missing: (fields: string) => string
    turnOnToTest: string
    testing: string
    testConnection: string
    updateKeys: string
    connect: string
  }
  carriers: {
    intro: string
    refreshing: string
    refreshFromShipStation: string
    colCarrier: string
    colServices: string
    colSources: string
    colActions: string
    loading: string
    noActiveSource: string
    servicesCount: (enabled: number, total: number) => string
    carrierActions: string
    sourceUspsDirect: string
    channelUspsDirect: string
    channelUpsDirect: string
    groups: { domestic: string; international: string }
    modalTitle: (carrier: string) => string
    close: string
    modalHint: string
    deselectAll: string
    selectAll: string
    cancel: string
    saving: string
    save: string
  }
  address: {
    title: string
    hint: string
    street: string
    city: string
    state: string
    zip: string
    validating: string
    validate: string
    unavailable: string
    deliverable: string
    notConfirmed: string
  }
}

export const shippingProvidersI18n: LocalizedDict<ShippingProvidersStrings> = {
  en: {
    hub: {
      title: 'Shipping providers',
      description:
        'Connect the channels PEXIFY buys labels through and choose which carrier services it may buy. Keys are encrypted and never shown again.',
      cheapestRule: 'Buys the cheapest quote among active channels',
      offRule: 'Turned-off channels are never quoted or bought from',
      internalRule: "PEXIFY Internal pays the carrier's actual charge",
      channelsTitle: '1 · Purchase channels',
      channelsHint: 'Where PEXIFY buys the label',
      loading: 'Loading providers…',
      loadError: 'Providers could not be loaded. Refresh the page to try again.',
      carriersTitle: '2 · Carriers & services',
      carriersHint: 'Who delivers the parcel',
      addressTitle: 'Address check',
      addressHint: 'Standardize a destination before buying',
    },
    roles: {
      avocore: 'Discounted USPS label reseller',
      byelabel: 'Discounted USPS · UPS · FedEx label reseller',
      shipdad: 'Discounted USPS · UPS · FedEx · DHL label reseller',
      uniuni: 'UniUni last-mile network · USPS Ground Advantage beyond it',
      stamps: 'USPS · UPS · FedEx from one connection',
      shipstation: 'Multi-carrier broker · Etsy address source',
      usps: 'Retail reference rate for external sellers',
    },
    uspsDirectLabel: 'USPS (direct)',
    card: {
      requestFailed: (status) => `Request failed (${status})`,
      notConnected: 'Not connected',
      off: 'Off',
      active: 'Active',
      keys: 'Keys',
      saved: 'saved',
      missingSr: 'missing',
      paidFrom: (account) => `Paid from its ${account} account`,
      balance: 'Balance',
      keysSaved: (label) => `${label} keys saved`,
      saveFailed: (label) => `Could not save ${label} keys`,
      isOn: (label) => `${label} is on`,
      isOff: (label) => `${label} is off`,
      switchFailed: (label) => `Could not switch ${label}`,
      activeSwitch: (label) => `${label} active`,
      accountLabel: 'Account label',
      savedPlaceholder: 'Saved — leave blank to keep',
      pastePlaceholder: 'Paste value',
      cancel: 'Cancel',
      saving: 'Saving…',
      saveKeys: 'Save keys',
      connectionWorks: 'Connection works — ',
      connectionFailed: 'Connection failed — ',
      cheapest: (amount) => ` Cheapest ${amount}.`,
      checked: (when) => `Checked ${when}`,
      never: 'never',
      missing: (fields) => `Missing ${fields}`,
      turnOnToTest: 'Turn the provider on to test it',
      testing: 'Testing…',
      testConnection: 'Test connection',
      updateKeys: 'Update keys',
      connect: 'Connect',
    },
    carriers: {
      intro: 'Every carrier PEXIFY can ship with, combined across all channels.',
      refreshing: 'Refreshing…',
      refreshFromShipStation: 'Refresh from ShipStation',
      colCarrier: 'Carrier',
      colServices: 'Services',
      colSources: 'Sources',
      colActions: 'Actions',
      loading: 'Loading…',
      noActiveSource: 'No active source',
      servicesCount: (enabled, total) => `${enabled} of ${total} services`,
      carrierActions: 'Carrier actions',
      sourceUspsDirect: 'USPS-direct',
      channelUspsDirect: 'USPS direct',
      channelUpsDirect: 'UPS direct',
      groups: { domestic: 'Domestic', international: 'International' },
      modalTitle: (carrier) => `${carrier} — Services`,
      close: 'Close',
      modalHint:
        'Selected services appear in the service dropdown when buying a label — whichever rate source quotes them.',
      deselectAll: 'Deselect All',
      selectAll: 'Select All',
      cancel: 'Cancel',
      saving: 'Saving…',
      save: 'Save',
    },
    address: {
      title: 'Validate address',
      hint: 'USPS-standardize a destination before buying a label (cuts returns).',
      street: 'Street line 1',
      city: 'City',
      state: 'State',
      zip: 'ZIP',
      validating: 'Validating…',
      validate: 'Validate',
      unavailable: 'Validation unavailable (connect a carrier first).',
      deliverable: '✓ Deliverable',
      notConfirmed: '⚠ Not confirmed',
    },
  },
  tr: {
    hub: {
      title: 'Kargo sağlayıcıları',
      description:
        'PEXIFY’ın etiket aldığı kanalları bağla ve hangi kargo hizmetlerini alabileceğini seç. Anahtarlar şifrelenir ve bir daha gösterilmez.',
      cheapestRule: 'Açık kanallar arasından en ucuz teklifi alır',
      offRule: 'Kapalı kanallardan fiyat sorulmaz, etiket alınmaz',
      internalRule: 'PEXIFY Internal kargo firmasının gerçek ücretini öder',
      channelsTitle: '1 · Satın alma kanalları',
      channelsHint: 'PEXIFY etiketi nereden alır',
      loading: 'Sağlayıcılar yükleniyor…',
      loadError: 'Sağlayıcılar yüklenemedi. Tekrar denemek için sayfayı yenile.',
      carriersTitle: '2 · Kargo firmaları ve hizmetler',
      carriersHint: 'Paketi kim teslim eder',
      addressTitle: 'Adres kontrolü',
      addressHint: 'Etiket almadan önce teslimat adresini standartlaştır',
    },
    roles: {
      avocore: 'İndirimli USPS etiket bayisi',
      byelabel: 'İndirimli USPS · UPS · FedEx etiket bayisi',
      shipdad: 'İndirimli USPS · UPS · FedEx · DHL etiket bayisi',
      uniuni: 'UniUni son kilometre ağı · ötesinde USPS Ground Advantage',
      stamps: 'Tek bağlantıdan USPS · UPS · FedEx',
      shipstation: 'Çok firmalı kargo aracısı · Etsy adres kaynağı',
      usps: 'Dış satıcılar için perakende referans fiyatı',
    },
    uspsDirectLabel: 'USPS (doğrudan)',
    card: {
      requestFailed: (status) => `İstek başarısız (${status})`,
      notConnected: 'Bağlı değil',
      off: 'Kapalı',
      active: 'Açık',
      keys: 'Anahtarlar',
      saved: 'kayıtlı',
      missingSr: 'eksik',
      paidFrom: (account) => `${account} hesabından ödenir`,
      balance: 'Bakiye',
      keysSaved: (label) => `${label} anahtarları kaydedildi`,
      saveFailed: (label) => `${label} anahtarları kaydedilemedi`,
      isOn: (label) => `${label} açıldı`,
      isOff: (label) => `${label} kapatıldı`,
      switchFailed: (label) => `${label} açılıp kapatılamadı`,
      activeSwitch: (label) => `${label} açık`,
      accountLabel: 'Hesap adı',
      savedPlaceholder: 'Kayıtlı — korumak için boş bırak',
      pastePlaceholder: 'Değeri yapıştır',
      cancel: 'İptal',
      saving: 'Kaydediliyor…',
      saveKeys: 'Anahtarları kaydet',
      connectionWorks: 'Bağlantı çalışıyor — ',
      connectionFailed: 'Bağlantı başarısız — ',
      cheapest: (amount) => ` En ucuz ${amount}.`,
      checked: (when) => `Son kontrol: ${when}`,
      never: 'hiç',
      missing: (fields) => `Eksik: ${fields}`,
      turnOnToTest: 'Test etmek için sağlayıcıyı aç',
      testing: 'Test ediliyor…',
      testConnection: 'Bağlantıyı test et',
      updateKeys: 'Anahtarları güncelle',
      connect: 'Bağla',
    },
    carriers: {
      intro: 'PEXIFY’ın gönderebildiği tüm kargo firmaları, tüm kanallar birleşik.',
      refreshing: 'Yenileniyor…',
      refreshFromShipStation: 'ShipStation’dan yenile',
      colCarrier: 'Kargo firması',
      colServices: 'Hizmetler',
      colSources: 'Kaynaklar',
      colActions: 'İşlemler',
      loading: 'Yükleniyor…',
      noActiveSource: 'Açık kaynak yok',
      servicesCount: (enabled, total) => `${total} hizmetten ${enabled} tanesi`,
      carrierActions: 'Kargo firması işlemleri',
      sourceUspsDirect: 'USPS-doğrudan',
      channelUspsDirect: 'USPS doğrudan',
      channelUpsDirect: 'UPS doğrudan',
      groups: { domestic: 'Yurt içi', international: 'Yurt dışı' },
      modalTitle: (carrier) => `${carrier} — Hizmetler`,
      close: 'Kapat',
      modalHint:
        'Seçili hizmetler etiket alırken hizmet listesinde görünür — hangi fiyat kaynağı teklif verirse.',
      deselectAll: 'Tüm seçimi kaldır',
      selectAll: 'Tümünü seç',
      cancel: 'İptal',
      saving: 'Kaydediliyor…',
      save: 'Kaydet',
    },
    address: {
      title: 'Adresi doğrula',
      hint: 'Etiket almadan önce adresi USPS standardına getir (iadeleri azaltır).',
      street: 'Sokak / cadde 1. satır',
      city: 'Şehir',
      state: 'Eyalet',
      zip: 'Posta kodu',
      validating: 'Doğrulanıyor…',
      validate: 'Doğrula',
      unavailable: 'Doğrulama kullanılamıyor (önce bir kargo firması bağla).',
      deliverable: '✓ Teslim edilebilir',
      notConfirmed: '⚠ Doğrulanamadı',
    },
  },
}
